import React, { useCallback, useEffect, useRef, useState } from "react"
import { Link } from "gatsby"
import { ArrowRightIcon, ChevronDownIcon } from "@heroicons/react/solid"

import StickyScrollWrapper from "../../common/stickyScrollWrapper"
import { readableNumber } from "../../../utils/text"

const SCROLL_SPY_OFFSET = 96

const desktopNavLinkClass = isActive =>
  [
    "block w-full rounded-md border-l-2 py-2 pl-3 pr-2 text-left text-sm font-medium transition-colors duration-150 no-underline",
    isActive
      ? "border-amber-400 bg-amber-50 text-amber-900"
      : "border-transparent text-gray-600 hover:border-amber-200 hover:bg-amber-50/60 hover:text-amber-900",
  ].join(" ")

const mobileNavLinkClass = isActive =>
  [
    "inline-flex flex-shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-colors no-underline sm:text-sm",
    isActive
      ? "bg-amber-100 text-amber-900"
      : "bg-gray-50 text-gray-600 hover:bg-amber-50 hover:text-amber-900",
  ].join(" ")

export const ExecutiveStatCard = ({ label, value }) => (
  <div className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2.5">
    <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
      {label}
    </dt>
    <dd className="mt-0.5 text-lg font-bold tabular-nums text-gray-900 sm:text-xl">
      {value}
    </dd>
  </div>
)

export const SummaryStatCard = ({ label, value, subtitle, large = false }) => (
  <div className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2.5 sm:px-4 sm:py-3">
    <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
      {label}
    </dt>
    <dd
      className={`mt-0.5 font-extrabold tabular-nums text-amber-600 ${
        large ? "text-xl sm:text-2xl" : "text-lg sm:text-xl"
      }`}
    >
      {value}
    </dd>
    {subtitle ? (
      <dd className="mt-0.5 text-xs text-gray-500">{subtitle}</dd>
    ) : null}
  </div>
)

export const ReportPanel = ({
  children,
  className = "",
  highlight = false,
  muted = false,
  compact = false,
  id,
}) => (
  <div
    id={id}
    className={`rounded-lg ${compact ? "p-4 sm:p-5" : "p-5 sm:p-6"} ${
      highlight
        ? "border border-amber-200 bg-gradient-to-br from-amber-50 to-white shadow-sm"
        : muted
        ? "border border-gray-100 bg-gray-50/60 shadow-none"
        : "bg-white shadow"
    } ${className}`.trim()}
  >
    {children}
  </div>
)

export const TakeawayCallout = ({ children }) => (
  <p className="mt-3 rounded-md border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-sm leading-relaxed text-gray-700">
    {children}
  </p>
)

export const MutedInsight = ({ children }) => (
  <p className="mt-3 text-sm leading-relaxed text-gray-500">{children}</p>
)

export const ReportSection = ({
  id,
  title,
  subtitle,
  insight,
  takeaway,
  highlightTakeaway = false,
  compact = false,
  children,
  className = "",
}) => (
  <section aria-labelledby={id || undefined} className={className}>
    <h2
      id={id}
      className={`${id ? "scroll-mt-28" : ""} font-bold text-gray-900 ${
        compact ? "text-base sm:text-lg" : "text-lg sm:text-xl"
      }`}
    >
      {title}
    </h2>
    {subtitle && !compact ? (
      <p className="mt-1 text-sm leading-relaxed text-gray-600">{subtitle}</p>
    ) : null}
    <div className={compact ? "mt-3" : "mt-4"}>{children}</div>
    {highlightTakeaway && takeaway ? (
      <TakeawayCallout>{takeaway}</TakeawayCallout>
    ) : null}
    {!highlightTakeaway && (insight || takeaway) ? (
      <MutedInsight>{insight || takeaway}</MutedInsight>
    ) : null}
  </section>
)

export const ReportGrid = ({ children, className = "" }) => (
  <ReportPanel className={className}>
    <div className="grid gap-6 lg:grid-cols-2 lg:items-start lg:gap-8">
      {children}
    </div>
  </ReportPanel>
)

export const ExecutiveSummaryCard = ({ topic, summary }) => {
  const ctaClass =
    "inline-flex items-center text-sm text-gray-700 no-underline hover:text-rose-700"

  if (!summary?.available) {
    return (
      <ReportPanel>
        <p className="text-sm leading-relaxed text-gray-700">
          {summary?.hero?.lead ||
            `Hive Index does not yet have ${topic.name} communities in the database.`}
        </p>
        {summary?.sourceLine ? (
          <p className="mt-2 text-xs text-gray-500">{summary.sourceLine}</p>
        ) : null}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
          <Link to={`/topics/${topic.slug}/`} className={ctaClass}>
            Browse all {topic.name} communities
            <ArrowRightIcon className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
          </Link>
          <Link to="/submit/" className={ctaClass}>
            Know a community we&apos;re missing? Add it
            <ArrowRightIcon className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </div>
      </ReportPanel>
    )
  }

  return (
    <ReportPanel compact={true}>
      <div className="grid gap-6 lg:grid-cols-2 lg:items-start lg:gap-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Executive Summary
          </p>
          {summary.intro ? (
            <p className="mt-2 text-sm leading-relaxed text-gray-700">
              {summary.intro}
            </p>
          ) : null}
          {summary.bigPicture ? (
            <p className="mt-2 text-sm leading-relaxed text-gray-700">
              {summary.bigPicture}
            </p>
          ) : null}
          {summary.sourceLine ? (
            <p className="mt-3 text-xs text-gray-500">{summary.sourceLine}</p>
          ) : null}
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
            <Link to={`/topics/${topic.slug}/`} className={ctaClass}>
              Browse all {topic.name} communities
              <ArrowRightIcon className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
            </Link>
            <Link to="/submit/" className={ctaClass}>
              Know a community we&apos;re missing? Add it
              <ArrowRightIcon className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-2.5">
          {summary.statCards.map(card => (
            <ExecutiveStatCard
              key={card.key}
              label={card.label}
              value={card.value}
            />
          ))}
        </dl>
      </div>
    </ReportPanel>
  )
}

export const ReportStickyLayout = ({ sections, children }) => {
  const [activeId, setActiveId] = useState(sections[0]?.id || "")
  const isClickScrolling = useRef(false)
  const clickScrollTimer = useRef(null)

  const scrollToSection = useCallback((event, id) => {
    event.preventDefault()
    const target = document.getElementById(id)
    if (!target) return

    isClickScrolling.current = true
    setActiveId(id)
    target.scrollIntoView({ behavior: "smooth", block: "start" })
    window.clearTimeout(clickScrollTimer.current)
    clickScrollTimer.current = window.setTimeout(() => {
      isClickScrolling.current = false
    }, 900)
  }, [])

  useEffect(() => {
    if (!sections.length) return undefined

    const updateActiveSection = () => {
      if (isClickScrolling.current) return

      let currentId = sections[0].id
      for (const section of sections) {
        const element = document.getElementById(section.id)
        if (!element) continue
        if (element.getBoundingClientRect().top <= SCROLL_SPY_OFFSET) {
          currentId = section.id
        }
      }
      setActiveId(currentId)
    }

    updateActiveSection()
    window.addEventListener("scroll", updateActiveSection, { passive: true })
    window.addEventListener("resize", updateActiveSection)

    return () => {
      window.removeEventListener("scroll", updateActiveSection)
      window.removeEventListener("resize", updateActiveSection)
      window.clearTimeout(clickScrollTimer.current)
    }
  }, [sections])

  if (!sections.length) {
    return <div className="space-y-4 sm:space-y-5">{children}</div>
  }

  return (
    <div id="report-body" data-report-body="">
      <nav
        aria-label="Report sections"
        className="sticky top-0 z-20 -mx-1 mb-3 border-b border-gray-100 bg-white/95 pb-2 pt-1 backdrop-blur-sm lg:hidden"
      >
        <p className="mb-1.5 px-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          In this report
        </p>
        <ul className="flex gap-1.5 overflow-x-auto pb-1">
          {sections.map(section => (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                onClick={event => scrollToSection(event, section.id)}
                className={mobileNavLinkClass(activeId === section.id)}
                aria-current={activeId === section.id ? "true" : undefined}
              >
                {section.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="lg:grid lg:grid-cols-5 lg:items-start lg:gap-8">
        <aside className="hidden lg:col-span-1 lg:block">
          <StickyScrollWrapper
            className="min-w-0 self-start"
            contentClassName="min-w-0"
            boundarySelector="[data-report-body]"
          >
            <nav
              aria-label="Report sections"
              className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm"
            >
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                In this report
              </p>
              <ul className="space-y-0.5">
                {sections.map(section => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      onClick={event => scrollToSection(event, section.id)}
                      className={desktopNavLinkClass(activeId === section.id)}
                      aria-current={
                        activeId === section.id ? "true" : undefined
                      }
                    >
                      {section.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </StickyScrollWrapper>
        </aside>

        <div className="min-w-0 space-y-4 sm:space-y-5 lg:col-span-4">
          {children}
        </div>
      </div>
    </div>
  )
}

export const ReportSectionAnchor = ({ id, children, className = "" }) => (
  <div
    id={id}
    className={`scroll-mt-28 space-y-4 sm:space-y-5 ${className}`.trim()}
  >
    {children}
  </div>
)

export const ReportGroupLabel = ({ children }) => (
  <div className="pt-0.5">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
      {children}
    </p>
    <div className="mt-2.5 border-b border-gray-100" aria-hidden="true" />
  </div>
)

export const CommunityLandscapeDivider = ({ topic }) => (
  <div id="report-overview" className="border-b border-gray-200 pb-1 pt-1">
    <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">
      {topic.name} Community Landscape
    </h2>
    <p className="mt-1 max-w-3xl text-sm text-gray-600">
      Where these groups live, how big they are, what they offer, and what Hive
      Index visitors click on.
    </p>
  </div>
)

/** @deprecated use CommunityLandscapeDivider */
export const FullReportDivider = CommunityLandscapeDivider

const ShareBar = ({ value }) => (
  <div className="mt-1 h-1.5 w-full max-w-[72px] rounded-full bg-gray-100">
    <div
      className="h-full rounded-full bg-amber-400"
      style={{ width: `${Math.max(4, value)}%` }}
    />
  </div>
)

export const PlatformSupplyDemandSection = ({
  items,
  insight,
  nested = false,
}) => {
  if (!items?.length) return null

  const section = (
    <>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] text-left text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3">Platform</th>
              <th className="py-2 pr-3">Communities</th>
              <th className="py-2 pr-3">Share of communities</th>
              <th className="py-2 pr-3">Outbound clicks</th>
              <th className="py-2">Share of clicks</th>
            </tr>
          </thead>
          <tbody>
            {items.slice(0, 8).map(item => (
              <tr
                key={item.slug}
                className="border-b border-gray-50 last:border-b-0"
              >
                <td className="py-2.5 pr-3 font-medium text-gray-800">
                  {item.name}
                </td>
                <td className="py-2.5 pr-3 tabular-nums text-gray-700">
                  {item.communityCount}
                </td>
                <td className="py-2.5 pr-3">
                  <span className="tabular-nums text-gray-600">
                    {item.communityPercent}%
                  </span>
                  <ShareBar value={item.communityPercent} />
                </td>
                <td className="py-2.5 pr-3 tabular-nums text-gray-700">
                  {readableNumber(item.clicks)}
                </td>
                <td className="py-2.5">
                  <span className="tabular-nums text-gray-600">
                    {item.clickPercent}%
                  </span>
                  <ShareBar value={item.clickPercent} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-gray-500">
        Share is calculated within this topic.
      </p>
    </>
  )

  if (nested) {
    return (
      <div className="mt-5 border-t border-gray-100 pt-4">
        <h3 className="text-sm font-semibold text-gray-900">
          Platform supply vs. clicks
        </h3>
        <div className="mt-3">{section}</div>
        {insight ? <MutedInsight>{insight}</MutedInsight> : null}
      </div>
    )
  }

  return (
    <ReportSection
      title="Platform Supply vs. User Interest"
      subtitle="Comparing where communities are listed with where Hive Index visitors click."
      insight={insight}
      compact={true}
    >
      {section}
    </ReportSection>
  )
}

export const CommunityQualitySection = ({
  topic,
  reviewBreakdown,
  quality,
  reviewChart,
}) => {
  if (!quality?.available) return null

  const isLimited = quality.mode === "limited"

  return (
    <ReportPanel compact={true}>
      <ReportSection
        title="Community Quality & Ratings"
        insight={isLimited ? null : quality.featureInsight}
        compact={false}
      >
        {isLimited ? (
          <>
            <p className="text-sm text-gray-600">{quality.limitedNotice}</p>
            {reviewBreakdown.averageRating ? (
              <p className="mt-2 text-xs text-gray-500">
                {reviewBreakdown.averageRating.toFixed(1)} average rating across{" "}
                {reviewBreakdown.reviewedCommunityCount}{" "}
                {reviewBreakdown.reviewedCommunityCount === 1
                  ? "community"
                  : "communities"}
                .
              </p>
            ) : null}
          </>
        ) : (
          <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Rating breakdown
              </h3>
              <div className="mt-2">{reviewChart}</div>
              <p className="mt-2 text-xs text-gray-500">
                {reviewBreakdown.totalReviews} review
                {reviewBreakdown.totalReviews === 1 ? "" : "s"} across{" "}
                {reviewBreakdown.reviewedCommunityCount} communit
                {reviewBreakdown.reviewedCommunityCount === 1 ? "y" : "ies"}
                {reviewBreakdown.averageRating
                  ? ` · ${reviewBreakdown.averageRating.toFixed(
                      1
                    )} average rating`
                  : ""}
                .
              </p>
            </div>

            {quality.highRatedFeatures?.available ? (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Among highly rated communities
                </h3>
                <ul className="mt-2 space-y-1.5">
                  {quality.highRatedFeatures.items.slice(0, 5).map(item => (
                    <li
                      key={item.slug}
                      className="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-2 py-1.5 text-sm"
                    >
                      <span className="font-medium text-gray-800">
                        {item.name}
                        {item.overIndex ? (
                          <span className="ml-1.5 text-xs font-normal text-amber-700">
                            over-indexes
                          </span>
                        ) : null}
                      </span>
                      <span className="flex-shrink-0 text-right tabular-nums text-gray-600">
                        <span className="font-medium">{item.percent}%</span>
                        {item.categoryPercent != null ? (
                          <span className="ml-1 text-xs text-gray-500">
                            vs {item.categoryPercent}% overall
                          </span>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-gray-500">
                  Based on {quality.highRatedFeatures.sampleSize} communities
                  rated 4+ stars.
                </p>
              </div>
            ) : null}
          </div>
        )}

        {quality.examples.length ? (
          <div
            className={
              isLimited ? "mt-3" : "mt-5 border-t border-gray-100 pt-4"
            }
          >
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {isLimited ? "Rated examples" : "Highly rated examples"}
            </h3>
            <ul className="mt-2 space-y-1.5">
              {quality.examples.map(community => (
                <li key={community.slug}>
                  <Link
                    to={`/communities/${community.slug}/`}
                    className="flex items-center justify-between gap-3 rounded-md border border-gray-100 px-3 py-2 no-underline hover:bg-gray-50"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {community.name}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {community.platformName}
                        {community.reviewAvg
                          ? ` · ${parseFloat(community.reviewAvg).toFixed(
                              1
                            )} stars`
                          : ""}
                        {community.members
                          ? ` · ${community.members} members`
                          : ""}
                      </p>
                    </div>
                    <ArrowRightIcon
                      className="h-4 w-4 flex-shrink-0 text-gray-400"
                      aria-hidden="true"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </ReportSection>
    </ReportPanel>
  )
}

export const TopicComparisonSection = ({ topic, comparison }) => {
  if (!comparison.available || !comparison.tableRows.length) return null

  return (
    <ReportSection
      title={`How ${topic.name} Differs From Other Categories`}
      compact={false}
    >
      {comparison.summary ? (
        <p className="mb-4 text-sm text-gray-600">{comparison.summary}</p>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[280px] text-left text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3">Metric</th>
              <th className="py-2 pr-3">{topic.name}</th>
              <th className="py-2">Hive Index average</th>
            </tr>
          </thead>
          <tbody>
            {comparison.tableRows.map(row => (
              <tr
                key={row.metric}
                className={`border-b border-gray-50 last:border-b-0 ${
                  row.highlight ? "bg-amber-50/40" : ""
                }`}
              >
                <td className="py-2 pr-3 text-gray-700">{row.metric}</td>
                <td
                  className={`py-2 pr-3 tabular-nums ${
                    row.highlight
                      ? "font-semibold text-gray-900"
                      : "font-medium text-gray-900"
                  }`}
                >
                  {row.topic}
                </td>
                <td className="py-2 tabular-nums text-gray-600">
                  {row.site ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ReportSection>
  )
}

export const UserInterestSection = ({
  userClickInsights,
  trafficStats,
  platformSupplyDemand,
  supplyDemandInsight,
  clickTakeaway,
  renderBarChart,
}) => {
  const hasSupplyDemand = platformSupplyDemand?.available
  const hasClickInsights = userClickInsights?.available
  const hasRedirects = Boolean(trafficStats?.topicRedirects)

  if (!hasSupplyDemand && !hasClickInsights && !hasRedirects) {
    return null
  }

  const outboundClicks =
    trafficStats?.topicRedirects || userClickInsights?.totalClicks
  const showOutboundStat = outboundClicks && (hasClickInsights || hasRedirects)

  return (
    <ReportSection
      title="What Visitors Click"
      insight={clickTakeaway}
      compact={false}
    >
      {showOutboundStat ? (
        <dl className="mb-4 sm:max-w-xs">
          <SummaryStatCard
            label="Outbound clicks / mo"
            value={readableNumber(outboundClicks)}
          />
        </dl>
      ) : null}

      {hasSupplyDemand ? (
        <PlatformSupplyDemandSection
          items={platformSupplyDemand.items}
          insight={supplyDemandInsight}
          nested={true}
        />
      ) : null}

      {userClickInsights?.charts?.length ? (
        <div
          className={`grid gap-4 sm:grid-cols-2 lg:grid-cols-3 ${
            hasSupplyDemand ? "mt-5 border-t border-gray-100 pt-4" : ""
          }`}
        >
          {userClickInsights.charts.map(chart => (
            <div key={chart.id}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {chart.title}
              </h3>
              <div className="mt-2">{renderBarChart(chart.items)}</div>
            </div>
          ))}
        </div>
      ) : null}

      {userClickInsights?.topCommunities?.length ? (
        <div
          className={
            userClickInsights.charts?.length || hasSupplyDemand
              ? "mt-5 border-t border-gray-100 pt-4"
              : ""
          }
        >
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Top-clicked listings
          </h3>
          <div className="mt-2">
            {renderBarChart(userClickInsights.topCommunities)}
          </div>
        </div>
      ) : null}
    </ReportSection>
  )
}

/** @deprecated use UserInterestSection */
export const UserClicksSection = UserInterestSection

export const FeatureMixSection = ({
  insight,
  commonFeatures,
  nicheFeatures,
  commonItems,
  nicheItems,
  onShowMoreCommon,
  onShowMoreNiche,
  renderFeatureTable,
}) => {
  const hasCommon = commonFeatures.length > 0
  const hasNiche = nicheFeatures.length > 0
  if (!hasCommon && !hasNiche) return null

  return (
    <ReportPanel>
      <ReportSection
        title="Feature Mix"
        subtitle="Common features appear in at least 15% of listings."
        insight={insight}
        compact={false}
      >
        <div
          className={`grid gap-6 ${
            hasCommon && hasNiche ? "lg:grid-cols-2" : ""
          }`}
        >
          {hasCommon ? (
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                Common Features
              </h3>
              <div className="mt-2">
                {renderFeatureTable(commonItems.items)}
              </div>
              {commonItems.hiddenCount ? (
                <button
                  type="button"
                  className="mt-2 text-xs font-semibold text-rose-500 hover:text-rose-600"
                  onClick={onShowMoreCommon}
                >
                  Show all ({commonFeatures.length} features)
                </button>
              ) : null}
            </div>
          ) : null}

          {hasNiche ? (
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                Niche Features
              </h3>
              <div className="mt-2">{renderFeatureTable(nicheItems.items)}</div>
              {nicheItems.hiddenCount ? (
                <button
                  type="button"
                  className="mt-2 text-xs font-semibold text-rose-500 hover:text-rose-600"
                  onClick={onShowMoreNiche}
                >
                  Show all ({nicheFeatures.length} features)
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </ReportSection>
    </ReportPanel>
  )
}

export const WhatThisMeansSection = ({ whatThisMeans }) => {
  if (!whatThisMeans?.available || !whatThisMeans.bullets?.length) return null

  return (
    <ReportPanel compact={true}>
      <ReportSection title="What this means" compact={false}>
        <ul className="space-y-2 text-sm leading-relaxed text-gray-700">
          {whatThisMeans.bullets.map(bullet => (
            <li key={bullet} className="flex gap-2">
              <span className="text-amber-500" aria-hidden="true">
                •
              </span>
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      </ReportSection>
    </ReportPanel>
  )
}

export const MethodologyFAQSection = ({ methodology, faq }) => (
  <ReportPanel compact={true} muted={true}>
    <h2 className="text-lg font-bold text-gray-900 sm:text-xl">
      Methodology & FAQ
    </h2>
    <div className="mt-3">
      <h3 className="text-sm font-semibold text-gray-900">Methodology</h3>
      <p className="mt-2 text-sm leading-relaxed text-gray-600">
        {methodology.paragraph}
      </p>
      <ul className="mt-2 space-y-1 text-sm text-gray-600">
        {methodology.bullets.map(bullet => (
          <li key={bullet} className="flex gap-2">
            <span className="text-gray-400">•</span>
            <span>{bullet}</span>
          </li>
        ))}
      </ul>
      {methodology.updatedDate &&
      !methodology.bullets.some(b => b.startsWith("Last updated")) ? (
        <p className="mt-2 text-xs text-gray-500">
          Last updated {methodology.updatedDate}
        </p>
      ) : methodology.bullets.length ? null : (
        <p className="mt-2 text-xs text-gray-500">Updated periodically</p>
      )}
    </div>
    {faq.length ? (
      <div className="mt-5 border-t border-gray-100 pt-4">
        <TopicStatsFAQ faq={faq} hideTitle={true} />
      </div>
    ) : null}
  </ReportPanel>
)

export const TopicStatsFAQ = ({ faq, hideTitle = false }) => {
  const [openIndex, setOpenIndex] = useState(null)

  if (!faq.length) return null

  return (
    <div>
      {hideTitle ? (
        <h3 className="text-sm font-semibold text-gray-900">FAQ</h3>
      ) : (
        <h2 id="faq" className="text-base font-bold text-gray-900 sm:text-lg">
          FAQ
        </h2>
      )}
      <dl className={`divide-y divide-gray-100 ${hideTitle ? "mt-2" : "mt-2"}`}>
        {faq.map((item, index) => {
          const isOpen = openIndex === index
          const answerId = `faq-answer-${index}`

          return (
            <div key={item.question}>
              <dt>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={answerId}
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="flex w-full items-center justify-between gap-3 py-3 text-left text-sm font-semibold text-gray-900"
                >
                  <span>{item.question}</span>
                  <ChevronDownIcon
                    className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                    aria-hidden="true"
                  />
                </button>
              </dt>
              <dd
                id={answerId}
                aria-hidden={!isOpen}
                className={`overflow-hidden text-sm leading-relaxed text-gray-600 transition-[max-height] duration-200 ${
                  isOpen ? "max-h-96 pb-3" : "max-h-0"
                }`}
              >
                {item.answer}
              </dd>
            </div>
          )
        })}
      </dl>
    </div>
  )
}

const FooterLinkRow = ({ label, links, withArrow = false }) => {
  if (!links?.length) return null

  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-3">
      <p className="w-32 flex-shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <ul className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
        {links.map(link => (
          <li key={link.href}>
            <Link
              to={link.href}
              className="inline-flex items-center font-medium text-gray-700 no-underline hover:text-rose-600"
            >
              {link.label}
              {withArrow ? (
                <ArrowRightIcon
                  className="ml-0.5 h-3.5 w-3.5"
                  aria-hidden="true"
                />
              ) : null}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

export const KeepExploringFooter = ({ footer }) => {
  if (!footer) return null

  const hasBrowse = footer.browse?.length > 0
  const hasReports = footer.reports?.length > 0
  const hasRelated = footer.relatedTopics?.length > 0
  const hasPlatforms = footer.browseByPlatform?.length > 0

  if (!hasBrowse && !hasReports && !hasRelated && !hasPlatforms) return null

  return (
    <div className="rounded-lg border border-gray-100 bg-gradient-to-br from-gray-50 to-white px-4 py-4 sm:px-5 sm:py-5">
      <h2 className="text-base font-bold text-gray-900 sm:text-lg">
        {footer.title}
      </h2>
      <div className="mt-3 space-y-2.5">
        {hasBrowse ? (
          <FooterLinkRow
            label="Browse"
            links={footer.browse}
            withArrow={true}
          />
        ) : null}
        {hasReports ? (
          <FooterLinkRow label="Related reports" links={footer.reports} />
        ) : null}
        {hasRelated ? (
          <FooterLinkRow label="Related topics" links={footer.relatedTopics} />
        ) : null}
        {hasPlatforms ? (
          <FooterLinkRow
            label="Browse by platform"
            links={footer.browseByPlatform}
          />
        ) : null}
      </div>
    </div>
  )
}
